import { AxiosResponse } from 'axios';
import request from '../utils/request';
import {
  ConditionDefinition,
  ConditionOperator,
  CorrelatedExpression,
  CorrelatedExpressionShallow,
  CorrelatedRule,
  CorrelatedRuleShallow,
} from 'src/pages/GlobalSetting/CorrelatedRuleTemplates/types';
import { EnableDisableStatus } from '@Policy/utils/types';

async function getRules(
  orgId: string,
  hideSocialGraph?: boolean
): Promise<AxiosResponse<CorrelatedRuleShallow[]>> {
  let url = `/riskAI/rules`;
  if (hideSocialGraph) {
    url = `${url}?hideSocialGraph=true`;
  }
  return await request({
    url,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

async function getRule(
  orgId: string,
  ruleId: string
): Promise<AxiosResponse<CorrelatedRule>> {
  const res = await request({
    url: `/riskAI/rules/${ruleId}`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
  try {
    res.data.raiRuleContent = JSON.parse(res.data.raiRuleContent);
  } catch (e) {
    console.error(e);
    res.data.raiRuleContent = [];
  }
  return res;
}

async function getExpressions(
  orgId: string
): Promise<AxiosResponse<CorrelatedExpressionShallow[]>> {
  return await request({
    url: `/riskAI/expressions`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

function createRule(orgId: string, data: CorrelatedRule) {
  const serializedData: any = { ...data };
  serializedData.raiRuleContent = JSON.stringify(data.raiRuleContent);
  delete serializedData.raiRuleId;
  delete serializedData.raiRuleBuiltIn;
  return request({
    url: `/riskAI/rules`,
    method: 'put',
    headers: { 'x-org-id': orgId },
    data: serializedData,
  });
}

function editRule(orgId: string, data: CorrelatedRule) {
  const serializedData: any = { ...data };
  serializedData.raiRuleContent = JSON.stringify(data.raiRuleContent);
  delete serializedData.raiRuleId;
  delete serializedData.raiRuleBuiltIn;
  return request({
    url: `/riskAI/rules/${data.raiRuleId}`,
    method: 'post',
    headers: { 'x-org-id': orgId },
    data: serializedData,
  });
}

function deleteRule(orgId: string, ruleId: string) {
  return request({
    url: `/riskAI/rules/${ruleId}`,
    method: 'delete',
    headers: { 'x-org-id': orgId },
  });
}

async function getExpression(
  orgId: string,
  expressionId: string
): Promise<AxiosResponse<CorrelatedExpression>> {
  const res = await request({
    url: `/riskAI/expressions/${expressionId}`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
  res.data.raiExpContent = JSON.parse(res.data.raiExpContent);
  return res;
}

function createExpression(orgId: string, data: CorrelatedExpression) {
  const serializedData: any = { ...data };
  serializedData.raiExpContent = JSON.stringify(data.raiExpContent);
  delete serializedData.raiExpId;
  delete serializedData.raiExpBuiltIn;
  delete serializedData.raiExpMarkers;
  return request({
    url: `/riskAI/expressions`,
    method: 'put',
    headers: { 'x-org-id': orgId },
    data: serializedData,
  });
}

function editExpression(orgId: string, data: CorrelatedExpression) {
  const serializedData: any = { ...data };
  serializedData.raiExpContent = JSON.stringify(data.raiExpContent);
  delete serializedData.raiExpId;
  delete serializedData.raiExpBuiltIn;
  delete serializedData.raiExpMarkers;
  return request({
    url: `/riskAI/expressions/${data.raiExpId}`,
    method: 'post',
    headers: { 'x-org-id': orgId },
    data: serializedData,
  });
}

function deleteExpression(orgId: string, expressionId: string) {
  return request({
    url: `/riskAI/expressions/${expressionId}`,
    method: 'delete',
    headers: { 'x-org-id': orgId },
  });
}

async function getConditions(
  orgId: string
): Promise<AxiosResponse<ConditionDefinition[]>> {
  const res = await request({
    url: `/riskAI/conditions`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
  res.data = res.data?.map((condition: ConditionDefinition) => {
    condition.raiCondValidOperators = (
      condition?.raiCondValidOperators as unknown as string
    )?.split(',') as ConditionOperator[];
    condition.raiCondFormat = JSON.parse(
      condition.raiCondFormat as unknown as string
    );
    return condition;
  });
  return res;
}

function getAnomalyConfigured(
  orgId: string
): Promise<AxiosResponse<EnableDisableStatus>> {
  return request({
    url: `/riskAI/anomalyConfigured`,
    method: 'get',
    headers: { 'x-org-id': orgId },
  });
}

interface EmailLoopMonitoringData {
  urls?: string[];
  urlDomains?: string[];
  senderAddresses?: string[];
  senderDomains?: string[];
}

interface EmailLoopMonitoringResponse {
  success: boolean;
  expressions: {
    id: string;
    action: 'created' | 'updated';
  }[];
}

function addEmailLoopMonitoring(
  orgId: string,
  data: EmailLoopMonitoringData
): Promise<AxiosResponse<EmailLoopMonitoringResponse>> {
  return request({
    url: `/riskAI/email-loop-monitoring`,
    method: 'post',
    headers: { 'x-org-id': orgId },
    data,
  });
}

export default {
  getRules,
  getRule,
  getExpressions,
  getExpression,
  createRule,
  editRule,
  deleteRule,
  createExpression,
  editExpression,
  deleteExpression,
  getConditions,
  getAnomalyConfigured,
  addEmailLoopMonitoring,
};
